import React, { useRef, useState } from "react";
import moment from "moment";
import { Outlet } from "react-router-dom";
import { Alert, Button, CircularProgress } from "@mui/material";
import { GroupOrderProvider, useGroupOrder } from "../../services/group-order/group-order.context";
import { parseCart } from "../../services/group-order/group-order.utils";
import LoginHeader from "../../Modules/Login/LoginHeader";
import Footer from "../../Modules/Common/footer";
import { GroupOrderBanner } from "./group-order-banner";

const centered = { padding: "48px 16px", textAlign: "center" as const };

const money = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });

const ReadOnlyCart = () => {
  const ctx = useGroupOrder();
  const lines = parseCart(ctx.groupOrder);
  if (!lines.length) return null;
  return (
    <ul className="group-readonly-cart" aria-label="Order items">
      {lines.map((line: any, i: number) => (
        <li key={line.uuid || i}>
          <span>
            {line.quantity} × {line.itemName}
          </span>
          <span>{money.format(Number(line.cartTotalPrice) || 0)}</span>
        </li>
      ))}
    </ul>
  );
};

const ClosedMessage = () => {
  const ctx = useGroupOrder();
  const go = ctx.groupOrder;
  const rep = go.repFirstName || "your rep";
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false); // synchronous guard against a fast double-click
  const onMakeChanges = async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    try {
      await ctx.makeChanges();
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };
  if (ctx.mode === "office" && go.status === "Ready") {
    return (
      <div className="container" style={centered}>
        <h3>
          Sent to {rep}
          {go.readyAt ? ` on ${moment(go.readyAt).format("ddd, MMM D [at] h:mm A")}` : ""}.
        </h3>
        <p>{rep} will review and place the order.</p>
        <Button variant="outlined" disabled={busy} onClick={onMakeChanges}>
          Make changes
        </Button>
        <ReadOnlyCart />
      </div>
    );
  }
  const text =
    go.status === "CheckingOut"
      ? `${rep} is checking out. This cart can't be changed right now.`
      : go.status === "Placed"
        ? `This order has been placed. Need a change? Contact ${rep}.`
        : "This admin order is closed.";
  return (
    <div className="container" style={centered}>
      <h3>{text}</h3>
      <ReadOnlyCart />
    </div>
  );
};

const ConflictNotice = () => {
  const ctx = useGroupOrder();
  if (!ctx.conflictNotice) return null;
  return (
    <div className="container">
      <Alert severity="info" onClose={ctx.dismissConflict} role="status">
        {ctx.conflictNotice}
      </Alert>
    </div>
  );
};

type ShellState =
  | "loading"
  | "loadFailed"
  | "notFound"
  | "linkInvalid"
  | "readOnly"
  | "hydrating"
  | "ready";

// One place decides what the shell shows, so the page and its frame can't disagree.
const shellState = (ctx: any): ShellState => {
  if (ctx.loading) return "loading";
  if (!ctx.groupOrder) return ctx.loadFailed ? "loadFailed" : "notFound";
  if (ctx.linkInvalid) return "linkInvalid";
  if (ctx.readOnly) return "readOnly";
  if (!ctx.hydrated) return "hydrating";
  return "ready";
};

const Spinner = () => (
  <div style={{ padding: 48, textAlign: "center" }}>
    <CircularProgress aria-label="Loading admin order" />
  </div>
);

const ShellContent = ({ state }: { state: ShellState }) => {
  const ctx = useGroupOrder();
  switch (state) {
    case "loading":
    case "hydrating":
      return <Spinner />;
    case "loadFailed":
      return (
        <div className="container" style={centered}>
          <h3>We couldn't load this admin order.</h3>
          <Button variant="contained" onClick={() => window.location.reload()}>
            Try again
          </Button>
        </div>
      );
    case "notFound":
    case "linkInvalid":
      return (
        <div className="container" style={centered}>
          <h3>
            {ctx.mode === "rep"
              ? "We couldn't find this admin order."
              : "This link isn't valid. Contact your rep."}
          </h3>
        </div>
      );
    case "readOnly":
      return (
        <>
          <GroupOrderBanner />
          <ConflictNotice />
          <ClosedMessage />
        </>
      );
    default:
      return (
        <>
          <GroupOrderBanner />
          <ConflictNotice />
          <Outlet />
        </>
      );
  }
};

// The caterer list and caterer page bring their own header and footer, and the
// brief loading spinner shows none, so nothing remounts when the data arrives.
// The pages that stay up (errors, "Sent to your rep", closed) get them here.
// LoginHeader shows the office the logo only.
const FRAMED: ShellState[] = ["loadFailed", "notFound", "linkInvalid", "readOnly"];

const ShellBody = () => {
  const state = shellState(useGroupOrder());
  if (!FRAMED.includes(state)) return <ShellContent state={state} />;
  return (
    <>
      <LoginHeader />
      <main className="group-shell-page">
        <ShellContent state={state} />
      </main>
      <Footer />
    </>
  );
};

export const GroupOrderShell = ({ mode }: { mode: "office" | "rep" }) => (
  <GroupOrderProvider mode={mode}>
    <ShellBody />
  </GroupOrderProvider>
);
